import { Box, Theme, createStyles, makeStyles } from '@material-ui/core';
import { AvailableStaff } from '@spike/available-staff-model';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { CalculatingSpinner } from 'components/UI';
import { DropDown } from 'components/Bookings2/List/Staff/DropDown';
import { StaffChip } from 'components/Bookings2/List/Staff/StaffChip';
import { ChangeEvent, FunctionComponent, MouseEvent, useEffect, useRef, useState } from 'react';
import { reduceResolution, wbp } from 'Theme';
import { DistributionRow } from './types';

interface TipDistributionRowProps {
    index: number;
    row: DistributionRow;
    availableStaff: Array<AvailableStaff>;
    loading?: boolean;
    onAmountChange: (amount: string) => void;
    onSelectStaff: (staff: AvailableStaff) => void;
    onRemove?: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            gap: 10,
            display: 'flex',
            overflow: 'hidden',
            flexWrap: 'nowrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: '1px solid #DDDDDD',
            borderRadius: '12px',

            [theme.breakpoints.down(wbp)]: {
                padding: `${reduceResolution(13)}px ${reduceResolution(18)}px`,
                marginBottom: `${reduceResolution(17)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                padding: '13px 18px',
                marginBottom: '17px'
            }
        },
        staffSlot: {
            flex: 1,
            minWidth: 0,
            position: 'relative'
        },
        staffChip: {
            maxWidth: '100%'
        },
        amountWrapper: {
            display: 'flex',
            alignItems: 'center',
            gap: 12
        },
        amountInputContainer: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #222222',
            borderRadius: '100px',
            padding: '0 16px',
            width: 109,
            height: 40,
            boxSizing: 'border-box'
        },
        amountInputContainerPlaceholder: {
            borderColor: '#DDDDDD'
        },
        amountInput: {
            'width': '100%',
            'border': 'none',
            'outline': 'none',
            'background': 'transparent',
            'textAlign': 'right',
            'fontFamily': 'Poppins',
            'fontSize': 14,
            'lineHeight': '21px',
            'color': '#222',
            'padding': 0,
            '&::placeholder': {
                color: '#A1A1A1'
            }
        },
        amountInputPlaceholder: {},
        trashSlot: {
            width: 24,
            height: 24,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
        },
        trashButton: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            color: '#222',
            fontSize: 18,
            width: '100%',
            height: '100%'
        }
    })
);

const TipDistributionRow: FunctionComponent<TipDistributionRowProps> = props => {
    const classes = useStyles();
    const staffSlotRef = useRef<HTMLDivElement>(null);

    const toRawDigits = (amount: string): string => {
        const num = parseFloat(amount) || 0;
        return Math.round(num * 100).toString();
    };

    const formatDigits = (digits: string): string => {
        const padded = digits.padStart(3, '0');
        const intPart = padded.slice(0, -2).replace(/^0+/, '') || '0';
        const decPart = padded.slice(-2);
        return `${intPart}.${decPart}`;
    };

    const [rawDigits, setRawDigits] = useState<string>(() => toRawDigits(props.row.amount));
    const [showDropDown, setShowDropDown] = useState(false);
    const [coords, setCoords] = useState({ x: 0, y: 0 });

    useEffect(() => {
        setRawDigits(toRawDigits(props.row.amount));
    }, [props.row.amount]);

    useEffect(() => {
        const handleClickOutside = (event: globalThis.MouseEvent | TouchEvent) => {
            if (showDropDown && staffSlotRef.current && !staffSlotRef.current.contains(event.target as Node)) {
                setShowDropDown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showDropDown]);

    const openDropDown = (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (props.row.source !== 'added' || props.row.staffId !== null) return;
        const rect = staffSlotRef.current?.getBoundingClientRect();
        if (rect) {
            setCoords({ x: rect.x, y: rect.y });
        }
        setShowDropDown(true);
    };

    const handleSelectStaff = (staff: AvailableStaff | null) => {
        setShowDropDown(false);
        if (staff) {
            props.onSelectStaff(staff);
        }
    };

    const handleAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
        const digits = event.target.value.replace(/[^\d]/g, '');
        if (digits.length <= 6) {
            setRawDigits(digits);
        }
    };

    const handleAmountBlur = () => {
        const formatted = formatDigits(rawDigits);
        if (formatted !== props.row.amount) {
            props.onAmountChange(formatted);
        }
    };

    const hasStaff = props.row.staffId !== null;
    const isAddedPlaceholder = props.row.source === 'added' && !hasStaff;

    return (
        <div className={classes.container}>
            <div
                ref={staffSlotRef}
                className={classes.staffSlot}
                onClick={event => event.stopPropagation()}
            >
                {showDropDown ? (
                    <DropDown
                        selectedId={null}
                        selectedAvatar={null}
                        selectedStaffName={null}
                        availableStaff={props.availableStaff}
                        x={coords.x}
                        y={coords.y}
                        onClick={handleSelectStaff}
                    />
                ) : (
                    <StaffChip
                        className={classes.staffChip}
                        name={hasStaff ? props.row.staffName ?? 'Unassigned' : 'Choose Staff'}
                        imageUrl={props.row.staffImageUrl ?? null}
                        onClickHandler={isAddedPlaceholder ? openDropDown : undefined}
                    />
                )}
            </div>

            <Box className={classes.amountWrapper}>
                {props.loading ? (
                    <CalculatingSpinner />
                ) : (
                    <Box
                        className={clsx(classes.amountInputContainer, {
                            [classes.amountInputContainerPlaceholder]: isAddedPlaceholder
                        })}
                    >
                        <input
                            id={`checkout_tips_value_${props.index}`}
                            className={clsx(classes.amountInput, {
                                [classes.amountInputPlaceholder]: isAddedPlaceholder
                            })}
                            type="text"
                            inputMode="decimal"
                            value={`$${formatDigits(rawDigits)}`}
                            placeholder="$0.00"
                            onChange={handleAmountChange}
                            onBlur={handleAmountBlur}
                            onFocus={event => event.currentTarget.select()}
                            onKeyDown={event => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    event.currentTarget.blur();
                                }
                            }}
                            onClick={event => event.stopPropagation()}
                        />
                    </Box>
                )}
                <span className={classes.trashSlot}>
                    {props.onRemove && (
                        <button
                            type="button"
                            className={classes.trashButton}
                            aria-label="Remove staff"
                            onClick={event => {
                                event.stopPropagation();
                                props.onRemove?.();
                            }}
                        >
                            <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                    )}
                </span>
            </Box>
        </div>
    );
};

export default TipDistributionRow;
